/**
 * Utility functions for form handling
 */

import { postData } from "../../../services/api";

/**
 * Sanitizes input to prevent XSS attacks
 * @param {string} input - The input string to sanitize
 * @returns {string} - The sanitized string
 */
const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
};

/**
 * Handles contact form submission
 * @param {Object} values - Form values
 * @param {Function} setSubmitting - Formik setSubmitting function
 * @param {Function} resetForm - Formik resetForm function
 * @param {Function} setAlertMessage - Function to set alert message
 * @param {Function} setShowAlert - Function to show/hide alert
 * @param {Function} setFormSubmitted - Function to set form submitted state
 * @param {Function} setShowContactForm - Function to show/hide contact form
 * @param {Function} setAlertType - Function to set alert type (success/error)
 */
export const handleContactSubmit = async (
  values,
  { setSubmitting, resetForm },
  setAlertMessage,
  setShowAlert,
  setFormSubmitted,
  setShowContactForm,
  setAlertType = null
) => {
  try {
    console.log("Starting contact form submission with values:", values);
    console.log("Current form state:", {
      isSubmitting: true,
      showAlert: false,
      formSubmitted: false,
    });

    // Sanitize all input values
    const sanitizedValues = {
      name: sanitizeInput(values.name),
      email: sanitizeInput(values.email),
      company: sanitizeInput(values.company),
      message: sanitizeInput(values.message),
    };

    const data = await postData("contact/submit", sanitizedValues);
    console.log("Contact form submission response:", data);

    // Check if the error is due to rate limiting
    if (data.status === 429) {
      const retryAfter = data.retryAfter || 30;
      throw new Error(
        `${data.message} Please try again in ${retryAfter} seconds.`
      );
    }

    // Check if the error is due to Firestore permission denied
    if (data.message && data.message.includes("permission_denied")) {
      console.error("Firestore permission denied:", data.message);
      throw new Error(
        "You've already submitted a message recently. Please try again later."
      );
    }

    // Check for Firestore index error
    if (
      data.message &&
      data.message.includes("FAILED_PRECONDITION") &&
      data.message.includes("requires an index")
    ) {
      console.error("Firestore index error:", data.message);
      throw new Error(
        "Our database is currently being configured. Please try again later or contact support directly."
      );
    }

    // Show success message
    console.log("Form submission successful, showing success message");
    if (setAlertType) setAlertType("success");
    setAlertMessage(
      "Your message has been sent successfully! We'll get back to you soon."
    );
    setShowAlert(true);
    setFormSubmitted(true);
    resetForm();

    console.log("Updated form state:", {
      isSubmitting: false,
      showAlert: true,
      formSubmitted: true,
    });

    // Close form after delay
    setTimeout(() => {
      console.log("Closing contact form");
      setShowContactForm(false);
      setFormSubmitted(false);
    }, 3000);
  } catch (error) {
    console.error("Error submitting contact form:", error);
    // Set error state before showing alert
    if (setAlertType) setAlertType("error");

    // Extract error message from server response
    let errorMessage;
    if (error.message && error.message.includes("HTTP error!")) {
      // Extract the message from the server response
      const match = error.message.match(/message: (.*?)(?:\n|$)/);
      errorMessage = match ? match[1] : "Server error occurred";
    } else if (
      error.name === "AbortError" ||
      error.message.includes("aborted") ||
      error.message.includes("Failed to fetch")
    ) {
      errorMessage =
        "Network error: Unable to connect to the server. Please check your internet connection and try again.";
    } else {
      errorMessage = error.message || "Failed to submit contact form";
    }

    setAlertMessage(errorMessage);
    setShowAlert(true);
  } finally {
    // Always set isSubmitting to false, even if there's an error
    setSubmitting(false);
  }
};
